import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SiteImagesProvider } from '@/contexts/SiteImagesContext';
import { SiteSettingsProvider } from '@/contexts/SiteSettingsContext';
import { Toaster } from '@/components/ui/sonner';
import SEOHead from '@/components/SEOHead';
import CookieBanner from '@/components/CookieBanner';
import AdminImageReplacer from '@/components/AdminImageReplacer';
import { ImpersonationBanner } from '@/components/ImpersonationControl';

// Layouts
import PublicLayout from '@/components/layout/PublicLayout';
import BackofficeLayout from '@/components/layout/BackofficeLayout';

// Public pages
import Home from '@/pages/Home';
import ChiSiamo from '@/pages/ChiSiamo';
import Servizi from '@/pages/Servizi';
import ServizioDettaglio from '@/pages/ServizioDettaglio';
import EquipeMedica from '@/pages/EquipeMedica';
import Contatti from '@/pages/Contatti';
import FAQ from '@/pages/FAQ';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import VerificaEmail from '@/pages/VerificaEmail';
import PasswordDimenticata from './pages/PasswordDimenticata';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiePolicy from '@/pages/CookiePolicy';
import OperazioneCultura from '@/pages/OperazioneCultura';

// Patient pages
import Prenota from '@/pages/Prenota';
import MieiAppuntamenti from '@/pages/MieiAppuntamenti';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';
import Profilo from '@/pages/Profilo';

// Public extra pages
import Eventi from '@/pages/Eventi';
import EventoDettaglio from '@/pages/EventoDettaglio';
import News from '@/pages/News';
import NewsDettaglio from '@/pages/NewsDettaglio';

// Backoffice pages
import Dashboard from '@/pages/backoffice/Dashboard';
import GestioneAppuntamenti from '@/pages/backoffice/GestioneAppuntamenti';
import GestioneDottori from '@/pages/backoffice/GestioneDottori';
import DottoreEdit from '@/pages/backoffice/DottoreEdit';
import GestioneServizi from '@/pages/backoffice/GestioneServizi';
import ServizioEdit from '@/pages/backoffice/ServizioEdit';
import GestioneStudi from '@/pages/backoffice/GestioneStudi';
import GestioneDisponibilita from '@/pages/backoffice/GestioneDisponibilita';
import Pazienti from '@/pages/backoffice/Pazienti';
import Impostazioni from '@/pages/backoffice/Impostazioni';
import GestioneEventi from '@/pages/backoffice/GestioneEventi';
import GestioneNews from '@/pages/backoffice/GestioneNews';
import GestioneMedia from '@/pages/backoffice/GestioneMedia';
import ConversazioniAI from '@/pages/backoffice/ConversazioniAI';
import Backup from '@/pages/backoffice/Backup';
import Ricollocazioni from '@/pages/backoffice/Ricollocazioni';
import WhatsAppInbox from '@/pages/backoffice/WhatsAppInbox';
import ConfigurazioneWhatsApp from '@/pages/backoffice/ConfigurazioneWhatsApp';

// Doctor area
import DoctorLayout from '@/components/layout/DoctorLayout';
import MieiAppuntamentiMedico from '@/pages/doctor/MieiAppuntamentiMedico';
import DisponibilitaMedico from '@/pages/doctor/DisponibilitaMedico';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
    <SiteImagesProvider>
    <SiteSettingsProvider>
      <BrowserRouter>
        <SEOHead />
        <CookieBanner />
        <AdminImageReplacer />
        <ImpersonationBanner />
        <Routes>
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/chi-siamo" element={<ChiSiamo />} />
            <Route path="/operazione-cultura" element={<OperazioneCultura />} />
            <Route path="/servizi" element={<Servizi />} />
            <Route path="/servizi/:id" element={<ServizioDettaglio />} />
            <Route path="/equipe" element={<EquipeMedica />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/eventi" element={<Eventi />} />
            <Route path="/eventi/:id" element={<EventoDettaglio />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:id" element={<NewsDettaglio />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registrati" element={<Register />} />
            <Route path="/verifica-email" element={<VerificaEmail />} />
            <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/prenota" element={<Prenota />} />
            <Route path="/i-miei-appuntamenti" element={<ProtectedRoute><MieiAppuntamenti /></ProtectedRoute>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/profilo" element={<ProtectedRoute><Profilo /></ProtectedRoute>} />
          </Route>

          {/* Backoffice routes */}
          <Route path="/backoffice" element={<ProtectedRoute roles={['operator','admin']}><BackofficeLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="appuntamenti" element={<GestioneAppuntamenti />} />
            <Route path="ricollocazioni" element={<Ricollocazioni />} />
            <Route path="whatsapp" element={<WhatsAppInbox />} />
            <Route path="whatsapp/configurazione" element={<ProtectedRoute roles={['admin']}><ConfigurazioneWhatsApp /></ProtectedRoute>} />
            <Route path="dottori" element={<GestioneDottori />} />
            <Route path="dottori/nuovo" element={<DottoreEdit />} />
            <Route path="dottori/:doctorId" element={<DottoreEdit />} />
            <Route path="servizi" element={<GestioneServizi />} />
            <Route path="servizi/nuovo" element={<ServizioEdit />} />
            <Route path="servizi/:serviceId" element={<ServizioEdit />} />
            <Route path="studi" element={<GestioneStudi />} />
            <Route path="disponibilita" element={<GestioneDisponibilita />} />
            <Route path="pazienti" element={<Pazienti patientsOnly />} />
            <Route path="utenti" element={<Pazienti />} />
            <Route path="eventi" element={<GestioneEventi />} />
            <Route path="news" element={<GestioneNews />} />
            <Route path="media" element={<GestioneMedia />} />
            <Route path="conversazioni-ai" element={<ConversazioniAI />} />
            <Route path="impostazioni" element={<Impostazioni />} />
            <Route path="backup" element={<ProtectedRoute roles={['admin']}><Backup /></ProtectedRoute>} />
          </Route>

          {/* Doctor area routes */}
          <Route path="/area-dottore" element={<ProtectedRoute roles={['doctor']}><DoctorLayout /></ProtectedRoute>}>
            <Route index element={<MieiAppuntamentiMedico />} />
            <Route path="disponibilita" element={<DisponibilitaMedico />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </SiteSettingsProvider>
    </SiteImagesProvider>
    </AuthProvider>
  );
}

export default App;
